import { useRef } from 'react';
import { m, useReducedMotion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import HexShape from './HexShape';

export default function CallToAction() {
  const shouldReduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });

  const mouseX = useMotionValue(50);
  const springX = useSpring(mouseX, { stiffness: 40, damping: 18 });

  const handleMouseMove = (e) => {
    if (shouldReduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mouseX.set(((e.clientX - r.left) / r.width) * 100);
  };

  const h1y = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const h2y = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const h1x = useTransform(springX, [0, 100], [-12, 12]);
  const h2x = useTransform(springX, [0, 100], [9, -9]);

  return (
    <section
      ref={ref}
      className="bg-primary py-28 sm:py-40 relative overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary-light/25 via-transparent to-primary-dark/35 pointer-events-none" />

      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <m.div
          className="absolute"
          style={{ top: '-10%', right: '-4%', x: shouldReduce ? 0 : h1x, y: shouldReduce ? 0 : h1y }}
        >
          <HexShape size={380} className="text-white" style={{ opacity: 0.15 }} />
        </m.div>
        <m.div
          className="absolute"
          style={{ bottom: '-8%', left: '-5%', x: shouldReduce ? 0 : h2x, y: shouldReduce ? 0 : h2y }}
        >
          <HexShape size={240} className="text-white" style={{ opacity: 0.12 }} />
        </m.div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-8 text-center">
        <m.p
          initial={shouldReduce ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-white/55 text-xs tracking-[0.2em] uppercase font-medium mb-8"
        >
          Ready to start?
        </m.p>

        <m.h2
          initial={shouldReduce ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: shouldReduce ? 0 : 0.09, ease: [0.22, 1, 0.36, 1] }}
          className="text-white font-extrabold leading-[0.93] tracking-tight mb-14"
          style={{ fontSize: 'clamp(2.25rem, 5vw, 4.5rem)' }}
        >
          Let's build something<br />
          made around you.
        </m.h2>

        <m.div
          initial={shouldReduce ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: shouldReduce ? 0 : 0.19, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col sm:flex-row items-center justify-center gap-5"
        >
          <m.a
            href="mailto:hello@vehi.systems"
            className="px-8 py-4 bg-white text-primary-dark font-semibold rounded-full text-sm hover:bg-surface transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            whileHover={shouldReduce ? undefined : { scale: 1.02 }}
            transition={{ type: 'spring', stiffness: 420, damping: 28 }}
          >
            Let's talk
          </m.a>
          <a
            href="mailto:hello@vehi.systems"
            className="text-white/65 text-sm font-medium hover:text-white transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            hello@vehi.systems
          </a>
        </m.div>
      </div>
    </section>
  );
}
